import { ProgressStepper } from '@/components/ProgressStepper';
import { TopBannerOnboarding } from '@/components/TopBannerOnboarding';
import { Citations, ExplainSlideButton, citations } from '@/components';
import { OnboardingProgressBar } from '@/components/OnboardingProgressBar';
import { FullScreenLoadingOverlay } from '@/components/FullScreenLoadingOverlay';
import { OnboardingSlideNavigation } from '@/components/OnboardingSlideNavigation';
import { OnboardingResultsStatusScreen } from '@/pages/OnboardingResultsStatusScreen';

import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '@/components/hooks/use-toast';
import { FunctionComponent, ReactNode, useEffect, useState } from 'react';

import {
  selectClient,
  selectFeatureFlags,
  selectClientIsLoading,
  selectSession,
  updateOnboardingSlideTracker,
} from '@/model';
import {
  selectSlides,
  onboardingSlideByIndex,
  isSelectedSlidesFiltered,
  onboardingSlideVideoUrls,
} from '@/components/OnboardingSlideInfo';
import { SessionType } from '@shared';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

interface OnboardingChromeProps {
  slidePage: FunctionComponent<ClientIdProps>;
  title?: ReactNode;
  children: ReactNode;
  citations?: (typeof citations)['value'][];
  nextButtonLabel: string;
  nextSlidePage?: FunctionComponent<ClientIdProps>;
  optionalCtaLabel?: string;
  optionalCtaPage?: FunctionComponent<ClientIdProps>;
  previousSlidePage?: FunctionComponent<ClientIdProps>;
  secondaryNextSlidePage?: FunctionComponent<ClientIdProps>;
  secondaryNextButtonLabel?: string;
  disableNextButton?: boolean;
  errorModalMessage?: string;
  shouldHideNextNavigation?: boolean;
  hideProgressStepper?: boolean;
  onNextClick?: () => void;
}

export function OnboardingChrome({
  slidePage,
  children,
  citations,
  nextButtonLabel,
  nextSlidePage,
  optionalCtaLabel,
  optionalCtaPage,
  secondaryNextButtonLabel,
  secondaryNextSlidePage,
  previousSlidePage,
  disableNextButton,
  errorModalMessage,
  shouldHideNextNavigation,
  hideProgressStepper,
  onNextClick,
}: OnboardingChromeProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient)!;
  const featureFlags = useSelector(selectFeatureFlags);
  const session = useSelector(selectSession);
  const { flags } = useFeatureFlags();
  const useFormProvider = flags.useFormProvider ?? false;
  const clientIsLoading = useSelector(selectClientIsLoading);

  // Check if form is incomplete and should show status screen
  const hasFormReference = !!client.formReferenceId;
  const isFormCompleted = client.formReferenceStatus === 'completed';
  const hasIntakeSurvey = !!client.intakeSurvey;
  const isClientSession = session.sessionType === SessionType.client;

  const shouldShowStatusScreen =
    hasFormReference &&
    !isFormCompleted &&
    !hasIntakeSurvey &&
    (isClientSession || useFormProvider);

  // Show status screen if form is incomplete (early return to avoid unnecessary calculations)
  if (shouldShowStatusScreen) {
    return (
      <OnboardingResultsStatusScreen
        variant="pending"
        onRefresh={() => window.location.reload()}
      />
    );
  }

  const selectedSlides = selectSlides(client, featureFlags);
  const relativeSlideIndex = selectedSlides.indexOf(slidePage);
  const globalSlideIndex = onboardingSlideByIndex.indexOf(slidePage);
  const selectedSlidesFiltered = isSelectedSlidesFiltered(selectedSlides);

  const { toast, dismiss } = useToast();

  const videoUrl = onboardingSlideVideoUrls.get(slidePage);

  // Store the toast ID to only dismiss our own toasts
  const [onboardingToastId, setOnboardingToastId] = useState<string | null>(null);

  useEffect(() => {
    dispatch(updateOnboardingSlideTracker(globalSlideIndex));
  }, [globalSlideIndex]);

  // Dismiss only our own toast messages when the next button is enabled.
  useEffect(() => {
    if (!disableNextButton && onboardingToastId) {
      dismiss(onboardingToastId);
      setOnboardingToastId(null);
    }
  }, [disableNextButton, dismiss, onboardingToastId]);

  return (
    <div id="top-container">
      <FullScreenLoadingOverlay isLoading={clientIsLoading} />
      {selectedSlidesFiltered && (
        <OnboardingProgressBar
          progressPercentage={(relativeSlideIndex / selectedSlides.length) * 100}
        />
      )}
      <div className="flex min-h-[800px] flex-col px-4 pb-[140px] pt-4 md:px-20">
        <TopBannerOnboarding className="px-2 md:px-0" />
        {!hideProgressStepper && (
          <ProgressStepper
            client={client}
            slideIndex={globalSlideIndex}
            show={!selectedSlidesFiltered}
          />
        )}
        <div className="relative">
          {children}
          {videoUrl && (
            <ExplainSlideButton
              videoUrl={videoUrl}
              className="hidden md:block"
            />
          )}
        </div>
        <Citations
          citations={citations}
          className="mt-6 py-4 md:mt-20"
        />
        <OnboardingSlideNavigation
          slidePage={slidePage}
          nextButtonLabel={nextButtonLabel}
          nextSlidePage={nextSlidePage}
          optionalCtaLabel={optionalCtaLabel}
          optionalCtaPage={optionalCtaPage}
          secondaryNextSlidePage={secondaryNextSlidePage}
          secondaryNextButtonLabel={secondaryNextButtonLabel}
          previousSlidePage={previousSlidePage}
          disableNextButton={disableNextButton}
          shouldHideNextNavigation={shouldHideNextNavigation}
          hideNavigationMenu={false}
          onNextClick={onNextClick}
          showErrorToast={message => {
            const toastResult = toast({
              title: 'Action Required',
              description: message || errorModalMessage || 'Please select an answer to continue',
              variant: 'default',
              className: 'border-yellow-500 bg-yellow-50 text-yellow-800',
            });
            setOnboardingToastId(toastResult.id);
          }}
          className="fixed bottom-0 left-0 right-0 w-full bg-white p-4 md:relative md:bottom-[20px] md:my-4 md:border-0 md:shadow-xl"
        />
      </div>
    </div>
  );
}
